import find from 'lodash/find';
import get from 'lodash/get';
import includes from 'lodash/includes';
import { FunctionComponent, useEffect, useRef, useState } from 'react';

import { logError } from '@savgroup-front-common/configuration/appInsights/AppInsights';
import { BUTTON_TYPES } from '@savgroup-front-common/constants';
import { Button } from '@savgroup-front-common/core/atoms/button';
import { usePlacesAutocomplete } from '@savgroup-front-common/core/atoms/Form/Autocomplete/PlacesAutocomplete/hooks';
import {
  geocodeByAddress,
  getLatLng,
} from '@savgroup-front-common/core/atoms/Form/Autocomplete/PlacesAutocomplete/helpers';
import Input from '@savgroup-front-common/core/atoms/Form/Input/Input';
import { InputGroup } from '@savgroup-front-common/core/atoms/Form/InputGroup';
import { Loader } from '@savgroup-front-common/core/atoms/loader';
import { safeFormattedIntlString } from '@savgroup-front-common/core/formatters';
import { PositionIcon } from '@savgroup-front-common/core/protons/icons';

import {
  $DropdownSuggestions,
  $LoadingSuggestion,
  $Suggestion,
} from './Address.styles';
import messages from './messages';

interface SearchAddress {
  address?: string;
  location?: {
    lat: number;
    lng: number;
  };
  countryCode?: string;
  city?: string;
  postalCode?: string;
  placeId?: string;
  maxDistance?: number;
}

interface UserAddress {
  addressId?: string;
  address?: string;
  city?: string;
  postalCode?: string;
  countryCode?: string;
}

interface AddressProps {
  searchAddress?: SearchAddress;
  onError?: (error: unknown) => void;
  userAddresses?: UserAddress[];
  onAddressSelected?: (address: SearchAddress) => void;
  isAdvancedOptionShown?: boolean;
  maxDistance: number;
}

const Address: FunctionComponent<AddressProps> = ({
  searchAddress,
  onAddressSelected,
}) => {
  const initialAddress = searchAddress?.address || '';
  const [address, setAddress] = useState(initialAddress);
  const addressRef = useRef(initialAddress);
  const [isGettingCurrentLocation, setIsGettingCurrentLocation] =
    useState(false);

  const geocodeLabel = ({
    address: label,
    countryCode,
    city,
    postalCode,
  }: {
    address?: string;
    countryCode?: string;
    postalCode?: string;
    city?: string;
  }) => {
    if (!label || !countryCode || !onAddressSelected) {
      return;
    }

    geocodeByAddress(label)
      .then((results) => getLatLng(results[0]))
      .then((latLng) => {
        onAddressSelected({
          address: label,
          location: latLng,
          countryCode,
          postalCode,
          city,
        });
      })
      .catch((error) => logError('Error in Address UI Component', error));
  };

  useEffect(() => {
    if (searchAddress) {
      geocodeLabel(searchAddress);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    places,
    handleChange,
    handleSelect,
    handleKeyDown,
    handleOpen,
    isOpen,
    showPlacesDropdown,
    showLoadingDropdown,
    selectedIndex,
    currentGooglePlaceIdLoading,
  } = usePlacesAutocomplete({
    value: address,
    countryCode: '',
    addSecondaryInformationOnSelect: true,
    onChange: (event) => {
      addressRef.current = event.target.value;
      setAddress(event.target.value);
    },
    onSelect: (details) => {
      if (
        !onAddressSelected ||
        !details.countryCode ||
        details.latitude === undefined ||
        details.longitude === undefined
      ) {
        return;
      }

      onAddressSelected({
        address: addressRef.current,
        location: { lat: details.latitude, lng: details.longitude },
        countryCode: details.countryCode,
        city: details.city,
        postalCode: details.postalCode,
      });
    },
  });

  const onSuccess = (pos: GeolocationPosition) => {
    if (!window.google?.maps) {
      return;
    }

    const geocoder = new window.google.maps.Geocoder();

    geocoder.geocode(
      { location: { lat: pos.coords.latitude, lng: pos.coords.longitude } },
      (results, status) => {
        if (status !== 'OK' || !results?.[0]) {
          logError(
            status === 'OK'
              ? 'No results found'
              : `Geocoder failed due to: ${status}`,
          );

          return;
        }

        const geocoded = results[0];
        const countryCode = get(
          find(geocoded.address_components, (info) =>
            includes(get(info, 'types'), 'country'),
          ),
          'short_name',
        );
        const postalCode = get(
          find(geocoded.address_components, (info) =>
            includes(get(info, 'types'), 'postal_code'),
          ),
          'short_name',
        );
        const city = get(
          find(geocoded.address_components, (info) =>
            includes(get(info, 'types'), 'locality'),
          ),
          'short_name',
        );

        setAddress(geocoded.formatted_address);
        addressRef.current = geocoded.formatted_address;
        geocodeLabel({
          address: geocoded.formatted_address,
          countryCode,
          postalCode,
          city,
        });
      },
    );
  };

  const getCurrentPosition = () => {
    setIsGettingCurrentLocation(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setIsGettingCurrentLocation(false);
        onSuccess(position);
      },
      (error) => {
        setIsGettingCurrentLocation(false);
        logError(error);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
    );
  };

  return (
    <>
      <InputGroup>
        <Input
          value={address}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={handleOpen}
          isLoading={isGettingCurrentLocation}
          selectAllOnFocus
          placeholder={messages.searchPlaces}
        />
        <Button
          primary
          hollow
          icon={<PositionIcon />}
          onClick={getCurrentPosition}
          type={BUTTON_TYPES.BUTTON}
          title={safeFormattedIntlString(messages.aroundMe)}
        />
      </InputGroup>
      {isOpen ? (
        <$DropdownSuggestions>
          {showLoadingDropdown && (
            <$LoadingSuggestion>
              <Loader />
            </$LoadingSuggestion>
          )}
          {showPlacesDropdown &&
            places?.map((place, index) => (
              <$Suggestion
                active={index === selectedIndex}
                key={place.id || place.description}
                onMouseDown={(event) => {
                  event.preventDefault();
                }}
                onClick={() => {
                  handleSelect(place);
                }}
              >
                {place.description}
                {currentGooglePlaceIdLoading === place.id && <Loader />}
              </$Suggestion>
            ))}
        </$DropdownSuggestions>
      ) : null}
    </>
  );
};

export default Address;
